import React, { useState } from 'react';
import { cx } from '../cx';

// D-09 §15.14 — 월 캘린더 (.cal / .cal-head / .cal-grid — 갤러리 §14.14 디자인)
// 선택일 groupTheme.primary 채움(b.sel) · 오늘 status.info 보더(b.today)

const DOW = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n: number) => String(n).padStart(2, '0');
const toKey = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

export interface CalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** 선택일 'YYYY-MM-DD' */
  value?: string;
  onChange?: (date: string) => void;
  /** 이 날짜 이전은 선택 불가 'YYYY-MM-DD' */
  minDate?: string;
  /** 이 날짜 이후는 선택 불가 'YYYY-MM-DD' */
  maxDate?: string;
  /**
   * 비작업일 'YYYY-MM-DD' — 지정하면 일요일과 이 날짜들을 빨강으로 표시한다(`DateRangePicker` 의 holidays 와 같은 규격).
   * 지정하지 않으면 요일 색을 입히지 않는다. 조회는 소비처가 한다(ui-core 는 네트워크를 모른다).
   */
  holidays?: Set<string>;
}

export function Calendar({ value, onChange, minDate, maxDate, holidays, className = '', ...props }: CalendarProps) {
  const today = new Date();
  const init = value ? new Date(`${value}T00:00:00`) : today;
  const [year, setYear] = useState(init.getFullYear());
  const [month, setMonth] = useState(init.getMonth() + 1); // 1~12

  const startOffset = new Date(year, month - 1, 1).getDay(); // 0=일
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayKey = toKey(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const move = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  return (
    <div className={cx('cal', className)} {...props}>
      <div className="cal-head">
        <button type="button" aria-label="이전 달" onClick={() => move(-1)}>‹</button>
        <span>{year}.{pad(month)}</span>
        <button type="button" aria-label="다음 달" onClick={() => move(1)}>›</button>
      </div>
      <div className="cal-grid">
        {DOW.map((d) => (
          <span key={d} className="dow">{d}</span>
        ))}
        {Array.from({ length: startOffset }, (_, i) => (
          <span key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
          const key = toKey(year, month, d);
          const blocked = (!!minDate && key < minDate) || (!!maxDate && key > maxDate);
          const sun = !!holidays && (new Date(year, month - 1, d).getDay() === 0 || holidays.has(key));
          return (
            <b
              key={d}
              className={cx(key === value && 'sel', key === todayKey && 'today', blocked && 'dis', sun && 'sun') || undefined}
              aria-disabled={blocked || undefined}
              onClick={onChange && !blocked ? () => onChange(key) : undefined}
            >
              {d}
            </b>
          );
        })}
      </div>
    </div>
  );
}
